import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { SellersService } from 'app/providers/api';
import { booleanOptions } from 'shared/constants/constants';
import { useApiMutation } from 'shared/hooks/useApiMutation';
import { useApiQuery } from 'shared/hooks/useApiQuery';
import { SellerIntegrationSettings } from 'shared/types/Sellers';
import { Loader } from 'shared/ui/Loader';

import { SellerIntegrationSettingsFormSkeleton } from './SellerIntegrationSettingsLoadingSkeleton';

import {
  SellerIntegrationSettingsFormValues,
  getSellerIntegrationSettingsFormValidationSchema,
  populateFormValuesFromSettings,
  populateSettingsFromFormValues,
} from '../utils/SellerIntegrationSettingsFormUtils';

interface SellerIntegrationSettingsFormProps {
  sellerId: string;
  title: string;
}

const SellerIntegrationSettingsForm = ({
  sellerId,
  title,
}: SellerIntegrationSettingsFormProps) => {
  const { t } = useTranslation();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [apiState, setApiState] = useState<{
    savedIntegrationSettings: SellerIntegrationSettings;
    initialValues: SellerIntegrationSettingsFormValues | null;
    hasLoaded: boolean;
  }>({
    savedIntegrationSettings: null,
    initialValues: null,
    hasLoaded: false,
  });

  const validationSchema = useMemo(
    () => getSellerIntegrationSettingsFormValidationSchema(t),
    [t],
  );

  // Load the Seller and hydrate the form initial values
  const { isLoading } = useApiQuery(
    ['fetchSeller', sellerId],
    () => SellersService.fetchSeller(sellerId),
    {
      onSuccess: (data) => {
        setApiState({
          savedIntegrationSettings: data,
          initialValues: populateFormValuesFromSettings(data),
          hasLoaded: true,
        });
      },
      cacheTime: 0, // Disable caching
    },
  );

  const { mutate: updateSeller, isLoading: isUpdating } = useApiMutation(
    ['updateSeller', sellerId],
    (values: SellerIntegrationSettings) =>
      SellersService.updateSeller({
        idOfSeller: sellerId,
        data: values,
      }),
    {
      onSuccess: (data) => {
        toast.success(t('Changes saved successfully'));
        setSubmitAttempted(false);
        setApiState({
          savedIntegrationSettings: data,
          initialValues: populateFormValuesFromSettings(data),
          hasLoaded: true,
        });
      },
    },
  );

  const handleSubmit = (values: SellerIntegrationSettingsFormValues) => {
    const settingsToSave = populateSettingsFromFormValues(values);
    updateSeller(settingsToSave);
  };

  if (isLoading || !apiState.initialValues) {
    return <SellerIntegrationSettingsFormSkeleton title={title} />;
  }

  return (
    <Formik
      initialValues={apiState.initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        setSubmitAttempted(true);
        handleSubmit(values);
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        errors,
        isValid,
        dirty,
        resetForm,
      }) => (
        <Form>
          <Box
            sx={{
              display: 'flex',
              marginBottom: '30px',
              gap: '20px',
              minHeight: '42px',
              maxWidth: '750px',
              alignItems: 'center',
            }}
          >
            <Typography
              component="h2"
              typography="openSans.h2"
              sx={{ flex: '1', marginBottom: '0px' }}
            >
              {title}
            </Typography>

            <Button
              color="primary"
              disabled={isUpdating || !dirty}
              onClick={() => {
                if (dirty) {
                  resetForm();
                  setSubmitAttempted(false);
                }
              }}
              sx={{
                display: dirty ? 'inline-flex' : 'none',
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              startIcon={isUpdating && <Loader color="inherit" />}
              type="submit"
              variant="contained"
              color="primary"
              disabled={(submitAttempted && !isValid) || isUpdating || !dirty}
              onClick={() => setSubmitAttempted(true)}
            >
              {t('Save')}
            </Button>
          </Box>
          <Typography
            sx={{
              maxWidth: '750px',
              marginBottom: '30px',
            }}
          >
            {t(
              // eslint-disable-next-line max-len
              'These settings control various aspects of the integration between RE-OPS and your Seller API.',
            )}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '750px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '750px',
              }}
            >
              {/* <Typography
                component="h4"
                typography="openSans.h4"
                sx={{ mb: '20px' }}
              >
                {t('Customer Activation Method')}
              </Typography>
              <Typography
                component="p"
                sx={{ mb: '30px' }}
              >
                {t(
                  // eslint-disable-next-line max-len
                  'This setting controls the customer account activation method. If set to “API Driven”, an activation is API-driven, utilizing the accept/decline/more info endpoints. If set to “Manual”, activation requires a manual, offline process involving account representatives for both new and existing customers.',
                )}
              </Typography>
              <FormControl fullWidth sx={{ mb: '30px', maxWidth: '400px' }}>
                <InputLabel>{t('Customer Activation Method')}</InputLabel>
                <Select
                  name="customerActivationMode"
                  label={t('Customer Activation Method')}
                  value={values.customerActivationMode}
                  onChange={handleChange}
                  fullWidth
                  error={
                    submitAttempted && Boolean(errors.customerActivationMode)
                  }
                >
                  {customerActivationModes.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}

              <Typography
                component="h4"
                typography="openSans.h4"
                sx={{ mb: '20px' }}
              >
                {t('Consolidated Pricing & Inventory Check API Calls')}
              </Typography>
              <Typography component="p" sx={{ mb: '30px' }}>
                {t(
                  // eslint-disable-next-line max-len
                  'RE-OPS can be configured to check product pricing and inventory with your Seller API using either separate calls or a single, combined call. This setting determines which method RE-OPS will use.',
                )}
              </Typography>
              <FormControl fullWidth sx={{ mb: '30px', maxWidth: '400px' }}>
                <InputLabel>
                  {t('Use Consolidated Pricing & Inventory Check API Calls')}
                </InputLabel>
                <Select
                  name="hasConsolidatedValidationCalls"
                  label={t(
                    'Use Consolidated Pricing & Inventory Check API Calls',
                  )}
                  value={values.hasConsolidatedValidationCalls}
                  onChange={handleChange}
                  fullWidth
                  error={
                    submitAttempted &&
                    Boolean(errors.hasConsolidatedValidationCalls)
                  }
                >
                  {booleanOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography
                component="h4"
                typography="openSans.h4"
                sx={{ mb: '20px' }}
              >
                {t('Customer Specific Pricing & Inventory Values')}
              </Typography>
              <Typography component="p" sx={{ mb: '30px' }}>
                {t(
                  // eslint-disable-next-line max-len
                  'RE-OPS allows for displaying both pricing and inventory with either global or customer-specific relevance. Customer-specific pricing accommodates individually negotiated rates. For inventory, the customer context can define regional availability based on factors like local warehouses.',
                )}
              </Typography>
              <FormControl fullWidth sx={{ mb: '30px', maxWidth: '400px' }}>
                <InputLabel>{t('Customer Specific Pricing')}</InputLabel>
                <Select
                  name="hasCustomerPricing"
                  label={t('Customer Specific Pricing')}
                  value={values.hasCustomerPricing}
                  onChange={handleChange}
                  fullWidth
                  error={submitAttempted && Boolean(errors.hasCustomerPricing)}
                >
                  {booleanOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: '30px', maxWidth: '400px' }}>
                <InputLabel>{t('Customer Specific Inventory')}</InputLabel>
                <Select
                  name="hasCustomerInventory"
                  label={t('Customer Specific Inventory')}
                  value={values.hasCustomerInventory}
                  onChange={handleChange}
                  fullWidth
                  error={
                    submitAttempted && Boolean(errors.hasCustomerInventory)
                  }
                >
                  {booleanOptions.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export { SellerIntegrationSettingsForm };
